import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Heading3, Label, Paragraph } from '@treams/treams-ui';

import Role from './Role';
import RolePlaceholder from './RolePlaceholder';

type Props = {
	user?: graphql.User;
	roles: graphql.UserRole[];
	refetch: () => Promise<unknown>;
};

export default function Managees(props: Props) {
	const { t } = useTranslation();

	const userRoles = props.roles.filter((role) => role.targetV2?.__typename === 'User');
	const groupRoles = props.roles.filter(
		(role) => role.targetV2?.__typename === 'Group' && role.role.id !== 'group-member',
	);

	return (
		<Flex>
			<Box flexGrow={0} flexShrink={0} width={200}>
				{props.user ? (
					<>
						<Heading3 mb={1}>
							{t('employees.settings.roles.managees.title', { name: props.user.firstName })}
						</Heading3>
						<Paragraph>
							{t('employees.settings.roles.managees.description', { name: props.user.firstName })}
						</Paragraph>
					</>
				) : (
					<>
						<Heading3.Skeleton />
						<Paragraph.Skeleton />
					</>
				)}
			</Box>
			<Box flexGrow={1} flexShrink={1} />
			<Box flexGrow={0} flexShrink={0} width={180 * 2 + 8}>
				<Flex>
					<Box mr={2} flexShrink={0} width={180}>
						<Label mb={1}>{t('employees.settings.roles.managees.people')}</Label>
						{props.user ? (
							<>
								{userRoles.map((role) => (
									<Box mb={1} key={role.targetV2!.id}>
										<Role
											me={props.user!}
											indirect={role.isIndirect}
											user={role.targetV2 as graphql.User}
											role={role.role as graphql.Role}
											refetch={props.refetch}
											externalReferenceID={role.externalReferenceID as string | undefined}
										/>
									</Box>
								))}
								<RolePlaceholder type="user" user={props.user} refetch={props.refetch} />
							</>
						) : (
							<Box mb={1}>
								<Role.Skeleton />
							</Box>
						)}
					</Box>
					<Box flexShrink={0} width={180}>
						<Label mb={1}>{t('employees.settings.roles.managees.groups')}</Label>
						{props.user ? (
							<>
								{groupRoles.map((role) => (
									<Box mb={1} key={role.targetV2!.id}>
										<Role
											me={props.user!}
											indirect={role.isIndirect}
											group={role.targetV2 as graphql.Group}
											role={role.role}
											refetch={props.refetch}
											externalReferenceID={role.externalReferenceID as string | undefined}
										/>
									</Box>
								))}
								<RolePlaceholder type="group" user={props.user} refetch={props.refetch} />
							</>
						) : (
							<Box mb={1}>
								<Role.Skeleton />
							</Box>
						)}
					</Box>
				</Flex>
			</Box>
			<Box flexGrow={1} flexShrink={1} />
		</Flex>
	);
}
